import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(err);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  acceptAgreement: () => api.post('/auth/accept-agreement'),
  updatePassword: (data) => api.put('/auth/password', data),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  resendValidation: () => api.post('/auth/resend-validation'),
  setupBilling: (data) => api.post('/auth/setup-billing', data),
  billingStatus: (sessionId) => api.get(`/auth/billing-status/${sessionId}`),
  // Google OAuth
  googleSession: (data) => api.post('/auth/google/session', data),
  // MFA
  mfaSetup: () => api.post('/auth/mfa/setup'),
  mfaVerifySetup: (data) => api.post('/auth/mfa/verify-setup', data),
  mfaDisable: (data) => api.post('/auth/mfa/disable', data),
  mfaStatus: () => api.get('/auth/mfa/status'),
  mfaVerifyLogin: (data) => api.post('/auth/mfa/verify-login', data),
};

// Languages
export const languagesAPI = { getAll: () => api.get('/languages') };

// Sessions
export const sessionsAPI = {
  create: (data) => api.post('/sessions', data),
  list: () => api.get('/sessions'),
  get: (id) => api.get(`/sessions/${id}`),
  end: (id) => api.post(`/sessions/${id}/end`),
  rate: (data) => api.post('/sessions/rate', data),
  acknowledgeRisk: (data) => api.post('/sessions/acknowledge-risk', data),
  escalate: (data) => api.post('/sessions/escalate', data),
};

// Translate
export const translateAPI = { translate: (data) => api.post('/translate', data) };

// Interpreter
export const interpreterAPI = {
  getProfile: () => api.get('/interpreter/profile'),
  setAvailability: (data) => api.post('/interpreter/availability', data),
  getJobs: () => api.get('/interpreter/jobs'),
  getFilteredJobs: (params) => api.get('/interpreter/jobs/filtered', { params }),
  acceptJob: (id) => api.post(`/interpreter/jobs/${id}/accept`),
  declineJob: (id) => api.post(`/interpreter/jobs/${id}/decline`),
  getEarnings: () => api.get('/interpreter/earnings'),
  getApprovedLanguages: () => api.get('/interpreter/approved-languages'),
  // Documents
  getDocuments: () => api.get('/interpreter/documents'),
  uploadDocument: (formData) => api.post('/interpreter/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Language Requests
  getLanguageRequests: () => api.get('/interpreter/language-requests'),
  createLanguageRequest: (data) => api.post('/interpreter/language-requests', data),
  uploadLanguageRequestDoc: (requestId, formData) => api.post(`/interpreter/language-requests/${requestId}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Invoices
  getInvoices: () => api.get('/interpreter/invoices'),
  createInvoice: (data) => api.post('/interpreter/invoices', data),
  getBillableSessions: () => api.get('/interpreter/billable-sessions'),
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getSessions: () => api.get('/admin/sessions'),
  getSlaCounters: () => api.get('/admin/sla-counters'),
  getLedger: (params) => api.get('/admin/ledger', { params }),
  // Org management
  listOrgs: () => api.get('/admin/orgs'),
  createOrg: (data) => api.post('/admin/orgs', data),
  getOrg: (id) => api.get(`/admin/orgs/${id}`),
  updateOrg: (id, data) => api.put(`/admin/orgs/${id}`, data),
  // Client Groups
  listClientGroups: (params) => api.get('/admin/client-groups', { params }),
  createClientGroup: (data) => api.post('/admin/client-groups', data),
  updateClientGroup: (id, data) => api.put(`/admin/client-groups/${id}`, data),
  deleteClientGroup: (id) => api.delete(`/admin/client-groups/${id}`),
  // Client management
  listClients: (params) => api.get('/admin/clients', { params }),
  createClient: (data) => api.post('/admin/clients', data),
  updateClient: (id, data) => api.put(`/admin/clients/${id}`, data),
  bulkUploadClients: (formData) => api.post('/admin/clients/bulk-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  sendClientValidation: (id) => api.post(`/admin/clients/${id}/send-validation`),
  validateClientEmail: (id) => api.post(`/admin/clients/${id}/validate-email`),
  // Interpreter management
  listInterpreters: (params) => api.get('/admin/interpreters', { params }),
  createInterpreter: (data) => api.post('/admin/interpreters', data),
  getInterpreter: (id) => api.get(`/admin/interpreters/${id}`),
  updateInterpreter: (id, data) => api.put(`/admin/interpreters/${id}`, data),
  approveInterpreter: (id) => api.post(`/admin/interpreters/${id}/approve`),
  rejectInterpreter: (id) => api.post(`/admin/interpreters/${id}/reject`),
  // Interpreter groups
  listInterpreterGroups: () => api.get('/admin/interpreter-groups'),
  createInterpreterGroup: (data) => api.post('/admin/interpreter-groups', data),
  updateInterpreterGroup: (id, data) => api.put(`/admin/interpreter-groups/${id}`, data),
  deleteInterpreterGroup: (id) => api.delete(`/admin/interpreter-groups/${id}`),
  // Language Requests
  listLanguageRequests: (params) => api.get('/admin/language-requests', { params }),
  approveLanguageRequest: (id) => api.post(`/admin/language-requests/${id}/approve`),
  rejectLanguageRequest: (id) => api.post(`/admin/language-requests/${id}/reject`),
  // Invoices
  listInvoices: (params) => api.get('/admin/invoices', { params }),
  approveInvoice: (id) => api.post(`/admin/invoices/${id}/approve`),
  rejectInvoice: (id) => api.post(`/admin/invoices/${id}/reject`),
  payInvoice: (id) => api.post(`/admin/invoices/${id}/pay`),
  // Fee Schedules
  listFeeSchedules: (params) => api.get('/admin/fee-schedules', { params }),
  createFeeSchedule: (data) => api.post('/admin/fee-schedules', data),
  getFeeSchedule: (id) => api.get(`/admin/fee-schedules/${id}`),
  updateFeeSchedule: (id, data) => api.put(`/admin/fee-schedules/${id}`, data),
  deleteFeeSchedule: (id) => api.delete(`/admin/fee-schedules/${id}`),
  // Pay Schedules
  listPaySchedules: () => api.get('/admin/pay-schedules'),
  createPaySchedule: (data) => api.post('/admin/pay-schedules', data),
  getPaySchedule: (id) => api.get(`/admin/pay-schedules/${id}`),
  updatePaySchedule: (id, data) => api.put(`/admin/pay-schedules/${id}`, data),
  addInterpretersToSchedule: (id, data) => api.post(`/admin/pay-schedules/${id}/add-interpreters`, data),
  removeInterpreterFromSchedule: (id, data) => api.post(`/admin/pay-schedules/${id}/remove-interpreter`, data),
  overrideInterpreterPay: (data) => api.post('/admin/interpreter-pay-override', data),
  // Profile template (legacy)
  getProfileTemplate: () => api.get('/admin/interpreter-template'),
  updateProfileTemplate: (data) => api.put('/admin/interpreter-template', data),
  // Forms (multi-type)
  listForms: () => api.get('/admin/forms'),
  getForm: (type) => api.get(`/admin/forms/${type}`),
  updateForm: (type, data) => api.put(`/admin/forms/${type}`, data),
  createFormType: (data) => api.post('/admin/forms', data),
  // Language management
  listLanguages: () => api.get('/admin/languages'),
  createLanguage: (data) => api.post('/admin/languages', data),
  updateLanguage: (id, data) => api.put(`/admin/languages/${id}`, data),
  deleteLanguage: (id) => api.delete(`/admin/languages/${id}`),
  // Credit bundles
  listCreditBundles: () => api.get('/admin/credit-bundles'),
  createCreditBundle: (data) => api.post('/admin/credit-bundles', data),
  updateCreditBundle: (id, data) => api.put(`/admin/credit-bundles/${id}`, data),
  deleteCreditBundle: (id) => api.delete(`/admin/credit-bundles/${id}`),
  // Subscription tiers
  listSubscriptionTiers: () => api.get('/admin/subscription-tiers'),
  createSubscriptionTier: (data) => api.post('/admin/subscription-tiers', data),
  updateSubscriptionTier: (id, data) => api.put(`/admin/subscription-tiers/${id}`, data),
  deleteSubscriptionTier: (id) => api.delete(`/admin/subscription-tiers/${id}`),
  // Org credits
  getOrgCredits: (orgId) => api.get(`/admin/credits/${orgId}`),
  adjustOrgCredits: (orgId, data) => api.post(`/admin/credits/${orgId}/adjust`, data),
  // Credit settings
  getCreditSettings: () => api.get('/credit-settings'),
  updateCreditSettings: (data) => api.put('/admin/credit-settings', data),
  // Referrals
  listReferrals: () => api.get('/admin/referrals'),
  // Shorthand aliases for dashboards
  getStats: () => api.get('/admin/stats'),
  getOrgs: () => api.get('/admin/orgs'),
  getInterpreters: () => api.get('/admin/interpreters'),
  getClients: () => api.get('/admin/clients'),
  getUsers: () => api.get('/org/users'),
  getInvoices: () => api.get('/admin/invoices'),
  getInvoice: (id) => api.get(`/admin/invoices/${id}`),
  getFeeSchedules: () => api.get('/admin/fee-schedules'),
  getPaySchedules: () => api.get('/admin/pay-schedules'),
  getLanguages: () => api.get('/admin/languages'),
  getLanguageRequests: () => api.get('/admin/language-requests'),
};

// Credits
export const creditsAPI = {
  getBalance: () => api.get('/credits/balance'),
  listBundles: () => api.get('/credits/bundles'),
  purchase: (data) => api.post('/credits/purchase', data),
  getSettings: () => api.get('/credit-settings'),
  // Stripe payments
  createCheckout: (data) => api.post('/payments/create-checkout', data),
  getPaymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Referral
export const referralAPI = {
  getLink: () => api.get('/referral/link'),
  getStats: () => api.get('/referral/stats'),
};

// Documents
export const documentsAPI = {
  upload: (interpreterId, formData) => api.post(`/interpreters/${interpreterId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  list: (interpreterId) => api.get(`/interpreters/${interpreterId}/documents`),
  approve: (docId) => api.post(`/documents/${docId}/approve`),
  reject: (docId) => api.post(`/documents/${docId}/reject`),
  downloadUrl: (docId) => `${API_BASE}/documents/${docId}/download`,
};

// Policies
export const policiesAPI = {
  get: (orgId) => api.get(`/policies/${orgId}`),
  update: (orgId, data) => api.put(`/policies/${orgId}`, data),
};

// Org
export const orgAPI = {
  getUsers: () => api.get('/org/users'),
  getDetails: () => api.get('/org/details'),
  updateDetails: (data) => api.put('/org/details', data),
  getMfaSetting: () => api.get('/org/mfa-setting'),
  updateMfaSetting: (data) => api.put('/org/mfa-setting', data),
};

// Pricing
export const pricingAPI = {
  list: () => api.get('/pricing-plans'),
  create: (data) => api.post('/pricing-plans', data),
};

// Seed
export const seedAPI = { seed: () => api.post('/seed') };

// Security Profiles
export const securityProfilesAPI = {
  list: () => api.get('/admin/security-profiles'),
  create: (data) => api.post('/admin/security-profiles', data),
  update: (profileId, data) => api.put(`/admin/security-profiles/${profileId}`, data),
  delete: (profileId) => api.delete(`/admin/security-profiles/${profileId}`),
  getMembers: (profileId) => api.get(`/admin/security-profiles/${profileId}/members`),
  addMember: (profileId, userId) => api.post(`/admin/security-profiles/${profileId}/members`, { user_id: userId }),
  removeMember: (profileId, userId) => api.delete(`/admin/security-profiles/${profileId}/members/${userId}`),
  getPermissions: () => api.get('/security-profiles/permissions'),
  getMyProfiles: () => api.get('/security-profiles/my-profiles'),
  seedBuiltins: () => api.post('/admin/security-profiles/seed-builtins'),
  getUserProfiles: (userId) => api.get(`/admin/users/${userId}/security-profiles`),
};

// Audit Log
export const auditLogAPI = {
  list: (params = {}) => api.get('/admin/audit-log', { params }),
};

// Phone Call System
export const callsAPI = {
  getSettings: () => api.get('/org/call-settings'),
  updateSettings: (data) => api.put('/org/call-settings', data),
  registerPhone: (data) => api.post('/org/register-phone', data),
  listPhones: () => api.get('/org/registered-phones'),
  unregisterPhone: (phone) => api.delete(`/org/registered-phones/${encodeURIComponent(phone)}`),
  setOrgPin: (orgId, pin) => api.post(`/admin/org-access-pin/${orgId}`, { pin }),
  getCallLog: () => api.get('/calls/log'),
  getActiveCalls: () => api.get('/calls/active'),
  getRateLimits: () => api.get('/calls/rate-limits'),
  getTwilioNumber: () => api.get('/calls/twilio-number'),
  setTwilioNumber: (data) => api.put('/calls/twilio-number', data),
  // Interpreter call endpoints
  getIncomingCalls: () => api.get('/interpreter/incoming-calls'),
  acceptCall: (callId) => api.post(`/interpreter/accept-call/${callId}`),
  declineCall: (callId) => api.post(`/interpreter/decline-call/${callId}`),
};

// Speech Services (Azure)
export const speechAPI = {
  transcribe: (formData) => api.post('/speech/transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  detectLanguage: (formData) => api.post('/speech/detect-language', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  synthesize: (data) => api.post('/speech/synthesize', data, { responseType: 'blob' }),
  getVoices: () => api.get('/speech/voices'),
  getStatus: () => api.get('/speech/status'),
};

// Bookings (pre-scheduled interpreter assignments)
export const bookingsAPI = {
  create: (data) => api.post('/bookings', data),
  list: (params) => api.get('/bookings', { params }),
  get: (id) => api.get(`/bookings/${id}`),
  cancel: (id) => api.put(`/bookings/${id}/cancel`),
  confirm: (id) => api.put(`/bookings/${id}/confirm`),
  start: (id) => api.put(`/bookings/${id}/start`),
  complete: (id) => api.put(`/bookings/${id}/complete`),
  accept: (id) => api.put(`/bookings/${id}/accept`),
  decline: (id) => api.put(`/bookings/${id}/decline`),
  handback: (id) => api.put(`/bookings/${id}/handback`),
  reconfirm: (id) => api.put(`/bookings/${id}/reconfirm`),
  interpreterList: () => api.get('/interpreter/bookings'),
  availableAssignments: () => api.get('/interpreter/available-assignments'),
  interpreterStats: () => api.get('/interpreter/stats'),
};

// Reports
export const reportsAPI = {
  getSchema: () => api.get('/reports/schema'),
  execute: (data) => api.post('/reports/execute', data),
  exportCSV: (data) => api.post('/reports/export/csv', data, { responseType: 'blob' }),
  exportExcel: (data) => api.post('/reports/export/excel', data, { responseType: 'blob' }),
  listSaved: () => api.get('/reports/saved'),
  getSaved: (id) => api.get(`/reports/saved/${id}`),
  saveReport: (data) => api.post('/reports/saved', data),
  updateSaved: (id, data) => api.put(`/reports/saved/${id}`, data),
  deleteSaved: (id) => api.delete(`/reports/saved/${id}`),
  // Schedules
  listSchedules: () => api.get('/reports/schedules'),
  createSchedule: (data) => api.post('/reports/schedules', data),
  updateSchedule: (id, data) => api.put(`/reports/schedules/${id}`, data),
  deleteSchedule: (id) => api.delete(`/reports/schedules/${id}`),
  runScheduleNow: (id) => api.post(`/reports/schedules/${id}/run-now`),
  getEmailLog: () => api.get('/reports/email-log'),
};

// Dashboards
export const dashboardsAPI = {
  list: () => api.get('/dashboards'),
  get: (id) => api.get(`/dashboards/${id}`),
  create: (data) => api.post('/dashboards', data),
  update: (id, data) => api.put(`/dashboards/${id}`, data),
  remove: (id) => api.delete(`/dashboards/${id}`),
  widgetData: (id, data) => api.post(`/dashboards/${id}/widget-data`, data),
};

// AI Models & Language-Model Mapping
export const aiModelsAPI = {
  list: () => api.get('/admin/ai-models'),
  create: (data) => api.post('/admin/ai-models', data),
  update: (id, data) => api.put(`/admin/ai-models/${id}`, data),
  remove: (id) => api.delete(`/admin/ai-models/${id}`),
  setDefault: (id) => api.put(`/admin/ai-models/${id}/set-default`),
  getLanguageMap: () => api.get('/admin/language-model-map'),
  setLanguageModel: (data) => api.put('/admin/language-model-map', data),
  bulkSetLanguageModels: (data) => api.put('/admin/language-model-map/bulk', data),
  // Org-level overrides
  getOrgLanguageMap: (orgId) => api.get('/admin/org-language-model-map', { params: orgId ? { org_id: orgId } : {} }),
  setOrgLanguageModel: (data) => api.put('/admin/org-language-model-map', data),
  bulkSetOrgLanguageModels: (data) => api.put('/admin/org-language-model-map/bulk', data),
};

// Invoice management (enhanced)
export const invoiceAPI = {
  get: (invoiceId) => api.get(`/admin/invoices/${invoiceId}`),
  approveItem: (invoiceId, itemIndex) => api.post(`/admin/invoices/${invoiceId}/approve-item`, { item_index: itemIndex }),
  batchApprove: (invoiceId, itemIndices) => api.post(`/admin/invoices/${invoiceId}/batch-approve`, { item_indices: itemIndices }),
};

export default api;
